/**
 * Counts time spent in Hapi: once a minute, while the tab is visible and
 * somebody has touched it in the last few minutes, the app tells the server
 * which page it is on. A tab left open overnight, or behind another window,
 * counts nothing. The server keeps one row per person per minute, so two tabs
 * open at once are still one minute.
 */

import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { api } from '../lib/api'

const EVERY_MS = 60_000
/** How long after the last click, key or scroll a person still counts as here. */
const IDLE_AFTER_MS = 3 * 60_000

export function useTimeCheckIn(enabled: boolean) {
  const location = useLocation()
  const page = useRef(location.pathname)
  page.current = location.pathname

  useEffect(() => {
    if (!enabled) return
    let last = Date.now()
    const touched = () => {
      last = Date.now()
    }
    const events = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const
    for (const e of events) window.addEventListener(e, touched, { passive: true })

    const beat = () => {
      if (document.visibilityState !== 'visible') return
      if (Date.now() - last > IDLE_AFTER_MS) return
      // Best effort: a missed minute is not worth an error on screen.
      void api.post('/me/time', { page: page.current }).catch(() => undefined)
    }
    beat()
    const timer = window.setInterval(beat, EVERY_MS)
    return () => {
      window.clearInterval(timer)
      for (const e of events) window.removeEventListener(e, touched)
    }
  }, [enabled])
}
